import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getMyTrainings } from '../../../services/trainingService';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';

const useMyTrainingApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getMyTrainings(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams]);

  const statusLabel = useMemo(() => ({
    assigned: t('training.assignmentStatus.assigned'),
    not_started: t('training.assignmentStatus.not_started'),
    in_progress: t('training.assignmentStatus.in_progress'),
    completed: t('training.assignmentStatus.completed'),
    failed: t('training.assignmentStatus.failed'),
    expired: t('training.assignmentStatus.expired'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'assignmentId', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'trainingTitle',
      label: t('training.title'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'categoryName',
      label: t('training.category'),
      options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    dateFilterColumn('dueDate', t('training.dueDate'), i18n.language),
    {
      name: 'status',
      label: t('training.status'),
      options: {
        filter: true,
        sort: true,
        filterType: 'dropdown',
        filterOptions: {
          names: ['not_started', 'in_progress', 'completed', 'failed', 'expired'],
          renderValue: (v) => statusLabel[v] || v,
        },
        customBodyRender: (value) => statusLabel[value] || value,
      },
    },
  ], [t, i18n.language, statusLabel]);

  return { columns, data, fetching, pagination };
};

export default useMyTrainingApi;
